import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { ChevronLeft, LogOut } from "lucide-react";
import { ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Area, AreaChart, Line, ComposedChart } from "recharts";

export default function PlayerHistory() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    api.get("/players/me/logs").then((r) => setLogs(r.data || []));
  }, []);

  const handleLogout = async () => { await logout(); nav("/", { replace: true }); };

  // Build last-30-days dataset (oldest → newest)
  const sorted = [...logs].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const last30 = sorted.slice(-30).map((l) => ({
    date: new Date(l.timestamp).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    volume: l.throw_volume || 0,
    arm: l.arm_health,
    sore: l.body_soreness,
    sleep: l.sleep_quality,
    mental: l.mental_energy,
    fatigue: l.post_throw_fatigue,
  }));

  const total30 = last30.reduce((acc, x) => acc + x.volume, 0);
  const maxDay = last30.reduce((m, x) => Math.max(m, x.volume), 0);
  const ready = logs.filter((l) => l.status === "ready").length;
  const critical = logs.filter((l) => l.status === "critical").length;

  return (
    <div className="min-h-screen pb-16" style={{ backgroundColor: "var(--ink-1)" }} data-testid="player-history-page">
      <header className="sticky top-0 z-30 backdrop-blur-xl border-b border-[var(--ink-border)]" style={{ backgroundColor: "rgba(10,20,36,0.85)" }}>
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center justify-between">
          <Logo size="sm" variant="lockup" linkTo="/portal" dark />
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white/70 hover:text-white hover:bg-white/5" data-testid="history-logout-button">
            <LogOut className="h-4 w-4 mr-1.5" /> Sign out
          </Button>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-5 pt-6">
        <Link to="/portal" className="inline-flex items-center text-sm text-white/50 hover:text-white">
          <ChevronLeft className="h-4 w-4" /> Back to today's log
        </Link>

        <div className="mt-4">
          <div className="text-[10px] tracking-[0.3em] font-bold text-white/40 uppercase">Your last 30 days</div>
          <h1 className="font-display font-black text-3xl tracking-tighter text-white mt-1">{user?.name}</h1>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-3">
          <div className="rounded-xl ink-card p-4">
            <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">30-Day Vol</div>
            <div className="font-display font-black text-3xl font-mono-num text-white mt-1" data-testid="history-total-volume">{total30}</div>
          </div>
          <div className="rounded-xl ink-card p-4">
            <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Peak Day</div>
            <div className="font-display font-black text-3xl font-mono-num text-white mt-1">{maxDay}</div>
          </div>
          <div className="rounded-xl ink-card p-4">
            <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Critical</div>
            <div className="font-display font-black text-3xl font-mono-num status-critical mt-1">{critical}</div>
          </div>
        </div>

        <div className="rounded-xl ink-card p-5 mt-5">
          <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Workload — last 30 days</div>
          <div className="h-48 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={last30} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="phVol" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--brand-secondary)" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="var(--brand-secondary)" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1F3354" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(last30.length / 8))} />
                <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #2C4570", backgroundColor: "#0F1E36", color: "#fff" }} />
                <Area type="monotone" dataKey="volume" stroke="var(--brand-secondary)" strokeWidth={2.5} fill="url(#phVol)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl ink-card p-5 mt-5">
          <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Readiness trends</div>
          <div className="h-48 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={last30} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1F3354" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(last30.length / 8))} />
                <YAxis domain={[0, 5]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #2C4570", backgroundColor: "#0F1E36", color: "#fff" }} />
                <Line type="monotone" dataKey="arm" stroke="#22D3EE" strokeWidth={2} dot={false} name="Arm" />
                <Line type="monotone" dataKey="sleep" stroke="#A78BFA" strokeWidth={2} dot={false} name="Sleep" />
                <Line type="monotone" dataKey="sore" stroke="#34D399" strokeWidth={2} dot={false} name="Soreness" />
                <Line type="monotone" dataKey="fatigue" stroke="#F87171" strokeWidth={2} dot={false} name="Fatigue" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/60 mt-2">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#22D3EE" }} /> Arm</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#A78BFA" }} /> Sleep</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#34D399" }} /> Sore</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#F87171" }} /> Fatigue</span>
          </div>
        </div>

        <div className="mt-6">
          <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Recent logs</div>
          <div className="rounded-xl ink-card overflow-hidden mt-3">
            <table className="w-full text-sm">
              <thead className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase border-b border-[var(--ink-border)]">
                <tr>
                  <th className="text-left px-3 py-2.5">Date</th>
                  <th className="text-center px-3 py-2.5">Arm</th>
                  <th className="text-left px-3 py-2.5">Env</th>
                  <th className="text-right px-3 py-2.5">Vol</th>
                  <th className="text-right px-3 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--ink-border)] text-white/80">
                {logs.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-white/40">No logs yet.</td></tr>}
                {logs.slice(0, 30).map((l) => (
                  <tr key={l.log_id}>
                    <td className="px-3 py-2.5 whitespace-nowrap">{new Date(l.timestamp).toLocaleDateString()}</td>
                    <td className="px-3 py-2.5 text-center font-mono-num">{l.arm_health}</td>
                    <td className="px-3 py-2.5">{l.throwing_environment}</td>
                    <td className="px-3 py-2.5 text-right font-mono-num font-semibold text-white">{l.throw_volume}</td>
                    <td className="px-3 py-2.5 text-right"><StatusBadge status={l.status} dot={false} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
